import React from 'react';

/**
 * Botón del sistema — Figma `Button` (3528:42981).
 *
 * Existía como clase CSS (`.wa-btn-*`, solo en formularios y modales) y como
 * copia a mano en cada pantalla. Fuera de los formularios cada sección elegía su
 * color: `ButtonBar` llegó a ofrecer una PALETA como prop —morado, azul, rojo,
 * verde, esmeralda, pizarra— y de ahí salían el «Nuevo socio» verde y el
 * «Cerrar caja» naranja. El sistema tiene un primario y es slate.
 *
 * Las cinco variantes, con sus valores leídos del componente de Figma:
 *
 *   primary      fondo `primary`        texto `primary-foreground`
 *   secondary    fondo `secondary`      texto `secondary-foreground`
 *   outline      borde 1 `border`       texto `foreground`
 *   destructive  fondo `destructive`    texto `destructive-foreground`
 *   ghost        sin fondo              texto `foreground`
 *
 * Tres tamaños: 32 / 36 / 40 de alto. El de en medio es el de la barra de
 * sección; el pequeño, para acciones dentro de una fila.
 */

/*
 * Los estados van por OPACIDAD, no por un segundo color. Es el modelo del
 * componente de Figma y es el mismo para las cinco variantes: hover 0.88
 * (0.85 en secondary), desactivado 0.40. Outline es la excepción — al no tener
 * fondo, su hover es un relleno `surface-subtle`.
 *
 * OJO al implementar por tu cuenta: el botón «Nueva entrada» de Entradas
 * oscurecía en hover con `--primary-strong`. No es lo que dice el frame, y con
 * dos modelos conviviendo el hover cambia de sentido según la pantalla.
 */
/*
 * Exportado a propósito: hay tres pantallas (Almacén, Personal, Superusuarios…)
 * cuyo bloque de móvil pinta `<button>` a mano porque necesita 44 px de alto y
 * ancho completo, tamaños que este componente no da. Antes cada una elegía su
 * color —verde, morado, lima— y el intento de arreglarlo fue pisar esas clases
 * desde un CSS con `!important`, que deja DOS verdades para el mismo botón.
 * Se comparte el mapa: el color sale de aquí, el tamaño lo pone la pantalla.
 */
export const VARIANTES_BOTON = {
  primary:
    'bg-[var(--primary)] text-[var(--primary-foreground)] hover:opacity-[0.88]',
  secondary:
    'bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:opacity-[0.85]',
  outline:
    'border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--surface-subtle)]',
  destructive:
    'bg-[var(--destructive)] text-[var(--destructive-foreground)] hover:opacity-[0.88]',
  // El frame no le da hover a ghost — un botón que no responde al pasar por
  // encima no parece pulsable, así que lleva el `muted` del sistema. Si el
  // diseño decide otra cosa, se cambia aquí y en el frame a la vez.
  ghost:
    'text-[var(--foreground)] hover:bg-[var(--muted)]',
};

const TAMANOS = {
  sm: 'h-8 gap-1.5 px-3',
  md: 'h-9 gap-2 px-4',
  lg: 'h-10 gap-2 px-6',
};

/** El icono acompaña al texto: 14 en el pequeño, 16 en los otros dos. */
export const TAMANO_ICONO = { sm: 14, md: 16, lg: 16 };

export default function Button({
  variant = 'primary',
  size = 'md',
  type = 'button',
  className = '',
  children,
  ...resto
}) {
  return (
    <button
      type={type}
      className={
        'inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-sm ' +
        'text-sm font-medium leading-5 transition-[background-color,opacity,border-color] ' +
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring-selected)] ' +
        'disabled:pointer-events-none disabled:opacity-40 ' +
        `${VARIANTES_BOTON[variant] || VARIANTES_BOTON.primary} ${TAMANOS[size] || TAMANOS.md} ${className}`
      }
      {...resto}
    >
      {children}
    </button>
  );
}
